import { useState } from "react";
import { useAlertContext } from "../../context/AlertContext";
import { formatDateRu } from "../../utils/dateFormat";
import { importAssetsFromContacts } from "../../services/assetsApi";
import { AssetHistoryList, holderText, KIND_LABELS, sinceText } from "./assetParts";

/**
 * Найденное общим поиском «Сотрудников» в технике, номерах и аккаунтах (правка №33):
 * у кого сейчас, кто держал последним (в т.ч. уволенные), вся история передач; плюс люди
 * с этим значением, которых нет в таблице (архив). Отдельной кнопки «Поиск по номеру» больше нет —
 * всё ищется из одной строки поиска (доработка №15, 04.11). [Claude] 2026-11-04
 */
export function AssetSearchResults({ assets = [], people = [], onOpenEmployee }) {
    if (!assets.length && !people.length) return null;
    return <section className="border-b border-slate-100 bg-slate-50/60 px-4 py-3" data-testid="asset-search-results">
        {assets.length ? <>
            <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Техника, номера, аккаунты</h3>
            <ul className="grid gap-2 lg:grid-cols-2">
                {assets.map((a) => <li key={a.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`asset-search-item-${a.id}`}>
                    <div className="text-sm font-semibold text-slate-900">{a.category}
                        {a.identifier ? <span className="ml-2 font-mono">{a.identifier}</span> : null}
                        {a.inventory_number ? <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">инв. {a.inventory_number}</span> : null}
                        <span className="ml-2 text-xs font-normal text-slate-400">{KIND_LABELS[a.kind] || ""}</span>
                    </div>
                    <div className="mt-1 text-sm">Сейчас: {a.holder?.type === "employee" && a.holder.employee_id
                        ? <button type="button" onClick={() => onOpenEmployee?.(a.holder.employee_id)} className="font-semibold text-amber-700 hover:underline">{holderText(a.holder)}</button>
                        : <b>{holderText(a.holder)}</b>} <span className="text-xs text-slate-400">{sinceText(a)}</span></div>
                    {a.last_employee && a.holder?.type !== "employee" ? <div className="text-sm">Последний сотрудник: <button type="button" onClick={() => onOpenEmployee?.(a.last_employee.employee_id)} className="font-semibold text-amber-700 hover:underline" data-testid={`asset-last-employee-${a.id}`}>{holderText(a.last_employee)}</button></div> : null}
                    <details className="mt-1 text-xs text-slate-500"><summary className="cursor-pointer">История передач</summary><div className="mt-1"><AssetHistoryList items={a.history} /></div></details>
                </li>)}
            </ul>
        </> : null}
        {people.length ? <>
            <h3 className="mb-1 mt-2 text-xs font-bold uppercase tracking-wide text-slate-400">Ещё люди с этим значением</h3>
            <ul className="flex flex-wrap gap-2">
                {people.map((p) => <li key={p.employee_id} className="rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={`asset-search-person-${p.employee_id}`}>
                    <button type="button" onClick={() => onOpenEmployee?.(p.employee_id)} className="text-sm font-semibold text-slate-900 hover:text-amber-700">{p.full_name}</button>
                    {p.date_fired ? <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">уволен {formatDateRu(p.date_fired)}</span> : null}
                    <div className="text-xs text-slate-500">{(p.matches || []).map((m) => `${m.field}: ${m.value}`).join(" · ")}</div>
                </li>)}
            </ul>
        </> : null}
    </section>;
}

/** «Заполнить технику из контактов»: рабочие и общие номера, Telegram и почта → предметы учёта (HR). */
export function FillAssetsFromContactsButton() {
    const { showAlert } = useAlertContext();
    const [importing, setImporting] = useState(false);
    const run = async () => {
        setImporting(true);
        try {
            const res = await importAssetsFromContacts();
            showAlert(`Из контактов заведено: ${res?.created ?? 0}, пропущено (уже есть или личные): ${res?.skipped ?? 0}`, "success");
        } catch (error) {
            showAlert(error.message || "Не удалось заполнить из контактов", "error");
        } finally {
            setImporting(false);
        }
    };
    return <button type="button" disabled={importing} onClick={run} className="text-xs text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline disabled:opacity-50" data-testid="assets-import-contacts" title="Рабочие и общие номера, Telegram и почта из контактов станут предметами учёта с текущим владельцем. Личные не переносятся. Повторный запуск дублей не создаёт.">{importing ? "Заполняем…" : "Технику из контактов"}</button>;
}

export default AssetSearchResults;
